import { useEffect, useState } from 'react';
import { Accordion, Alert, Badge, Card, Col, Nav, Row, Tab, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ApiDocsPayloadType, ApiDocsRouteType } from '@econtractor/types';
import * as apiSettingsDirs from 'api/apiSettingsDirs';
import { API_DOC_EXAMPLES } from './examples';
import {
    ROUTE_EXAMPLES,
    ROUTE_FIELDS,
    ROUTE_ID_BY_KEY,
    ROUTE_NOTES_KEYS,
    ROUTE_PATH_FIELDS,
    ROUTE_QUERY_FIELDS,
    WEBHOOK_IDS,
    routeKey,
    type ApiDocFieldDef,
    type ApiDocRouteId,
    type ApiDocWebhookId,
} from './routeConfig';
import { getCurrencyI18nParams } from 'utils/helpers/currency/currencyHelpers';

const CodeBlock = ({ children }: { children: string }) => (
    <pre className="mb-0 small bg-light p-2 rounded border overflow-auto" style={{ maxHeight: 320 }}>
        {children}
    </pre>
);

const JsonBlock = ({ value }: { value: unknown }) => (
    <CodeBlock>{JSON.stringify(value, null, 2)}</CodeBlock>
);

const QuickStartStep = ({ step, title, text }: { step: number; title: string; text: string }) => (
    <div className="col-12 col-md-4">
        <div className="border rounded p-3 bg-light bg-opacity-25 h-100">
            <div className="d-flex align-items-start gap-3">
                <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle border border-primary text-primary fw-semibold flex-shrink-0"
                    style={{ width: 30, height: 30, fontSize: 13, lineHeight: 1 }}
                >
                    {step}
                </span>
                <div>
                    <div className="fw-semibold">{title}</div>
                    <small className="text-muted d-block mt-1">{text}</small>
                </div>
            </div>
        </div>
    </div>
);

const FieldTable = ({
    fields,
    tPrefix,
    t,
}: {
    fields: ApiDocFieldDef[];
    tPrefix: string;
    t: (key: string, options?: Record<string, unknown>) => string;
}) => {
    if (!fields.length) return null;
    return (
        <Table responsive className="table-sm align-middle mb-0" style={{ minWidth: 420 }}>
            <thead>
                <tr className="bg-light">
                    <th style={{ width: 180 }}>{t('apiDocs.table.field')}</th>
                    <th style={{ width: 110 }}>{t('apiDocs.table.required')}</th>
                    <th>{t('apiDocs.table.description')}</th>
                </tr>
            </thead>
            <tbody>
                {fields.map(({ key, i18nKey }) => (
                    <tr key={key}>
                        <td>
                            <code>{key}</code>
                        </td>
                        <td>
                            {t(`${tPrefix}.${i18nKey}.required`, {
                                ...getCurrencyI18nParams(),
                                defaultValue: '—',
                            })}
                        </td>
                        <td className="text-break">
                            {t(`${tPrefix}.${i18nKey}.description`, {
                                ...getCurrencyI18nParams(),
                                defaultValue: '—',
                            })}
                        </td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );
};

const getRouteId = (route: ApiDocsRouteType): ApiDocRouteId | null =>
    ROUTE_ID_BY_KEY[routeKey(route.method, route.path)] || null;

const ApiDocs = () => {
    const { t } = useTranslation(['Settings']);
    const [docs, setDocs] = useState<ApiDocsPayloadType | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        const run = async () => {
            setIsLoading(true);
            setHasError(false);
            const result = await apiSettingsDirs.getApiDocs();
            if (!result) {
                setHasError(true);
                setIsLoading(false);
                return;
            }
            setDocs(result as ApiDocsPayloadType);
            setIsLoading(false);
        };
        run();
    }, []);

    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://your-crm.com';
    const basePath = docs?.basePath || '/api/integration/1.0';
    const fullBase = `${origin}${basePath}`;

    const curlAuthExample = [
        `curl -X POST "${fullBase}/clients" \\`,
        `  -H "Authorization: Bearer YOUR_API_KEY" \\`,
        `  -H "Content-Type: application/json" \\`,
        `  -d '${JSON.stringify({
            clientName: 'John Smith',
            clientPhone: '+380971112233',
        })}'`,
    ].join('\n');

    const renderExamples = (routeId: ApiDocRouteId) => {
        const examples = ROUTE_EXAMPLES[routeId];
        if (!examples?.length) return null;

        if (examples.length === 1) {
            const ex = examples[0];
            return (
                <div className="mt-3">
                    <div className="fw-semibold mb-1">{t('apiDocs.example')}</div>
                    {ex.hintKey && (
                        <p className="small text-muted mb-2">
                            {t(ex.hintKey, getCurrencyI18nParams())}
                        </p>
                    )}
                    <JsonBlock value={ex.payload} />
                </div>
            );
        }

        return (
            <div className="mt-3">
                <div className="fw-semibold mb-2">{t('apiDocs.examplesTitle')}</div>
                <Tab.Container defaultActiveKey={examples[0].id}>
                    <Nav variant="pills" className="flex-wrap gap-1 mb-2">
                        {examples.map((ex) => (
                            <Nav.Item key={ex.id}>
                                <Nav.Link eventKey={ex.id} className="py-1 px-2 small">
                                    {t(ex.labelKey)}
                                </Nav.Link>
                            </Nav.Item>
                        ))}
                    </Nav>
                    <Tab.Content>
                        {examples.map((ex) => (
                            <Tab.Pane key={ex.id} eventKey={ex.id}>
                                {ex.hintKey && (
                                    <p className="small text-muted mb-2">
                                        {t(ex.hintKey, getCurrencyI18nParams())}
                                    </p>
                                )}
                                <JsonBlock value={ex.payload} />
                            </Tab.Pane>
                        ))}
                    </Tab.Content>
                </Tab.Container>
            </div>
        );
    };

    const renderRoute = (route: ApiDocsRouteType, eventKey: string) => {
        const routeId = getRouteId(route);
        const title = routeId
            ? t(`apiDocs.routes.${routeId}.title`)
            : route.description || route.path;
        const description = routeId ? t(`apiDocs.routes.${routeId}.description`) : route.description;
        const when = routeId ? t(`apiDocs.routes.${routeId}.when`, { defaultValue: '' }) : '';
        const responseText = routeId
            ? t(`apiDocs.routes.${routeId}.response`, { defaultValue: '' })
            : '';
        const fields = routeId ? ROUTE_FIELDS[routeId] || [] : [];
        const queryFields = routeId ? ROUTE_QUERY_FIELDS[routeId] || [] : [];
        const pathFields = routeId ? ROUTE_PATH_FIELDS[routeId] || [] : [];
        const noteKeys = routeId ? ROUTE_NOTES_KEYS[routeId] || [] : [];

        return (
            <Accordion.Item key={`${route.method}-${route.path}`} eventKey={eventKey}>
                <Accordion.Header>
                    <div className="d-flex flex-column align-items-start gap-1 pe-2">
                        <span className="fw-semibold">{title}</span>
                        <span className="d-flex flex-wrap align-items-center gap-2">
                            <Badge bg={route.method === 'POST' ? 'primary' : 'secondary'}>{route.method}</Badge>
                            <code>{route.path}</code>
                            {route.permission && (
                                <Badge bg="light" text="dark" className="border">
                                    {route.permission}
                                </Badge>
                            )}
                        </span>
                    </div>
                </Accordion.Header>
                <Accordion.Body>
                    {description && <p className="mb-2">{description}</p>}
                    {when && (
                        <Alert variant="info" className="py-2 small mb-3">
                            <strong>{t('apiDocs.whenToUse')}:</strong> {when}
                        </Alert>
                    )}

                    {!!pathFields.length && (
                        <div className="mb-3">
                            <div className="fw-semibold mb-1">{t('apiDocs.pathParams')}</div>
                            <FieldTable fields={pathFields} tPrefix={`apiDocs.routes.${routeId}.pathParams`} t={t} />
                        </div>
                    )}

                    {!!queryFields.length && (
                        <div className="mb-3">
                            <div className="fw-semibold mb-1">{t('apiDocs.query')}</div>
                            <FieldTable fields={queryFields} tPrefix={`apiDocs.routes.${routeId}.query`} t={t} />
                        </div>
                    )}

                    {!!fields.length && (
                        <div className="mb-3">
                            <div className="fw-semibold mb-1">{t('apiDocs.request')}</div>
                            {!!route.request?.required?.length && (
                                <div className="small text-muted mb-2">
                                    {t('apiDocs.required')}: {route.request.required.join('; ')}
                                </div>
                            )}
                            <FieldTable fields={fields} tPrefix={`apiDocs.routes.${routeId}.fields`} t={t} />
                        </div>
                    )}

                    {routeId && renderExamples(routeId)}

                    {!!noteKeys.length && (
                        <div className="mt-3">
                            <div className="fw-semibold mb-1">{t('apiDocs.notes')}</div>
                            <ul className="mb-0 small">
                                {noteKeys.map((key) => (
                                    <li key={key}>{t(key, getCurrencyI18nParams())}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="mt-3">
                        <div className="fw-semibold mb-1">{t('apiDocs.response')}</div>
                        {responseText ? (
                            <p className="small mb-2">{responseText}</p>
                        ) : typeof route.response === 'string' ? (
                            <p className="small mb-2">
                                <code>{route.response}</code>
                            </p>
                        ) : null}
                        {typeof route.response === 'object' && route.response?.success && (
                            <>
                                <div className="small text-muted mb-1">{t('apiDocs.success')}</div>
                                <JsonBlock value={route.response.success} />
                            </>
                        )}
                                        {typeof route.response === 'object' && !!route.response?.errors?.length && (
                                            <>
                                                <div className="small text-muted mt-2 mb-1">{t('apiDocs.errors')}</div>
                                                <ul className="mb-0 small">
                                                    {route.response.errors.map((err) => {
                                                        const hintCode = err.split(' ')[0];
                                                        const hint = t(`apiDocs.errorHints.${hintCode}`, {
                                                            defaultValue: '',
                                                        });
                                                        return (
                                                            <li key={err}>
                                                                <code>{err}</code>
                                                                {hint ? ` — ${hint}` : ''}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </>
                                        )}
                    </div>
                </Accordion.Body>
            </Accordion.Item>
        );
    };

    const renderRoutes = (routes: ApiDocsRouteType[] = [], keyPrefix: string) => {
        if (!routes.length) return <span className="text-muted">—</span>;
        return (
            <Accordion alwaysOpen>
                {routes.map((route, index) => renderRoute(route, `${keyPrefix}-${index}`))}
            </Accordion>
        );
    };

    const rateLimit = docs?.auth?.rateLimit;
    const rateLimitText =
        rateLimit?.windowMs && rateLimit?.maxRequests
            ? t('apiDocs.rateLimitValue', {
                  max: rateLimit.maxRequests,
                  seconds: Math.round(rateLimit.windowMs / 1000),
                  scope: rateLimit.scope || '',
              })
            : '—';

    return (
        <Row>
            <Col sm={12}>
                <div className="w-100 d-md-flex pt-1 justify-content-between align-items-start mb-3 mb-md-2">
                    <div className="pb-1">
                        <h4 className="p-0 m-0">{t('apiDocs.title')}</h4>
                        <small className="text-muted">{t('apiDocs.subtitle')}</small>
                    </div>
                </div>

                {hasError && <Alert variant="danger">{t('apiDocs.loadError')}</Alert>}

                <Card className="mb-3">
                    <Card.Body>
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <i className="bi bi-rocket-takeoff text-primary fs-5" />
                            <h6 className="mb-0">{t('apiDocs.quickStart.title')}</h6>
                        </div>
                        <div className="row g-2">
                            <QuickStartStep
                                step={1}
                                title={t('apiDocs.quickStart.step1.title')}
                                text={t('apiDocs.quickStart.step1.text')}
                            />
                            <QuickStartStep
                                step={2}
                                title={t('apiDocs.quickStart.step2.title')}
                                text={t('apiDocs.quickStart.step2.text')}
                            />
                            <QuickStartStep
                                step={3}
                                title={t('apiDocs.quickStart.step3.title')}
                                text={t('apiDocs.quickStart.step3.text')}
                            />
                        </div>
                    </Card.Body>
                </Card>

                {isLoading ? (
                    <Card>
                        <Card.Body className="text-muted">...</Card.Body>
                    </Card>
                ) : !docs ? (
                    <Alert variant="warning">{t('apiDocs.empty')}</Alert>
                ) : (
                    <Card>
                        <Card.Body>
                            <Accordion alwaysOpen defaultActiveKey={['overview']}>
                                <Accordion.Item eventKey="overview">
                                    <Accordion.Header>{t('apiDocs.sections.overview')}</Accordion.Header>
                                    <Accordion.Body>
                                        <h6 className="mb-2">{t('apiDocs.basePath')}</h6>
                                        <p className="small text-muted mb-1">{t('apiDocs.basePathHint')}</p>
                                        <CodeBlock>{fullBase}</CodeBlock>
                                        <div className="small text-muted mt-1">
                                            {t('apiDocs.version')}: v{docs.version || '1.0'}
                                        </div>

                                        <hr />
                                        <h6 className="mb-2">{t('apiDocs.auth')}</h6>
                                        <p className="mb-2">{t('apiDocs.authDescription')}</p>
                                        <ul className="small mb-2">
                                            <li>
                                                <code>{docs.auth?.token?.authorizationHeader || 'Authorization: Bearer <API_KEY>'}</code>
                                            </li>
                                            <li>
                                                <code>{docs.auth?.token?.fallbackHeader || 'X-API-Key: <API_KEY>'}</code>
                                            </li>
                                        </ul>
                                        <div className="fw-semibold small mb-1">{t('apiDocs.curlExample')}</div>
                                        <CodeBlock>{curlAuthExample}</CodeBlock>

                                        <hr />
                                        <h6 className="mb-2">{t('apiDocs.note')}</h6>
                                        <p className="mb-0">{t('apiDocs.domainControlDescription')}</p>

                                        <hr />
                                        <h6 className="mb-2">{t('apiDocs.rateLimit')}</h6>
                                        <p className="mb-0">{rateLimitText}</p>

                                        <hr />
                                        <h6 className="mb-2">{t('apiDocs.permissionsTitle')}</h6>
                                        <p className="small text-muted mb-2">{t('apiDocs.permissionsHint')}</p>
                                        <Row className="g-2">
                                            <Col md={6}>
                                                <div className="fw-semibold small mb-1">{t('apiDocs.permissionsIn')}</div>
                                                <ul className="small mb-0">
                                                    {(docs.permissions?.write || []).map((code) => (
                                                        <li key={code}>
                                                            <code>{code}</code>
                                                            {' — '}
                                                            {t(`apiDocs.permissionLabels.${code}`, { defaultValue: code })}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </Col>
                                            <Col md={6}>
                                                <div className="fw-semibold small mb-1">{t('apiDocs.permissionsRead')}</div>
                                                <ul className="small mb-0">
                                                    {(docs.permissions?.read || []).map((code) => (
                                                        <li key={code}>
                                                            <code>{code}</code>
                                                            {' — '}
                                                            {t(`apiDocs.permissionLabels.${code}`, { defaultValue: code })}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </Col>
                                        </Row>
                                    </Accordion.Body>
                                </Accordion.Item>

                                <Accordion.Item eventKey="inbound-post">
                                    <Accordion.Header>{t('apiDocs.inboundPost')}</Accordion.Header>
                                    <Accordion.Body>
                                        <p className="text-muted small mb-3">{t('apiDocs.inboundPostHint')}</p>
                                        {renderRoutes(docs.inbound?.post, 'post')}
                                    </Accordion.Body>
                                </Accordion.Item>

                                <Accordion.Item eventKey="inbound-get">
                                    <Accordion.Header>{t('apiDocs.inboundGet')}</Accordion.Header>
                                    <Accordion.Body>
                                        <p className="text-muted small mb-3">{t('apiDocs.inboundGetHint')}</p>
                                        {renderRoutes(docs.inbound?.get, 'get')}
                                    </Accordion.Body>
                                </Accordion.Item>

                                <Accordion.Item eventKey="outbound">
                                    <Accordion.Header>{t('apiDocs.outboundEvents')}</Accordion.Header>
                                    <Accordion.Body>
                                        <p className="mb-2">{t('apiDocs.webhook.intro')}</p>
                                        <Alert variant="secondary" className="py-2 small">
                                            {t('apiDocs.webhook.howTo')}
                                        </Alert>

                                        <h6 className="mb-2">{t('apiDocs.webhook.delivery')}</h6>
                                        <ul className="small mb-3">
                                            <li>
                                                {t('apiDocs.webhook.method')}:{' '}
                                                <code>{docs.outbound?.webhook?.method || 'POST'}</code>
                                            </li>
                                            <li>
                                                {t('apiDocs.webhook.contentType')}:{' '}
                                                <code>{docs.outbound?.webhook?.contentType || 'application/json'}</code>
                                            </li>
                                            <li>
                                                {t('apiDocs.webhook.timeout')}:{' '}
                                                {docs.outbound?.webhook?.timeoutMs || 10000} ms
                                            </li>
                                        </ul>

                                        <h6 className="mb-2">{t('apiDocs.outboundEnvelope')}</h6>
                                        <p className="small text-muted mb-2">{t('apiDocs.webhook.envelopeHint')}</p>
                                        <JsonBlock value={API_DOC_EXAMPLES.webhookEnvelope} />

                                        <hr />
                                        <h6 className="mb-2">{t('apiDocs.webhook.eventsTitle')}</h6>
                                        <Accordion alwaysOpen>
                                            {WEBHOOK_IDS.map((code: ApiDocWebhookId, index) => {
                                                const serverEvent = docs.outbound?.events?.find((e) => e.code === code);
                                                const fieldEntries = Object.keys(serverEvent?.dataFields || {});
                                                return (
                                                    <Accordion.Item key={code} eventKey={`out-event-${index}`}>
                                                        <Accordion.Header>
                                                            <div className="d-flex flex-column align-items-start gap-1">
                                                                <span className="fw-semibold">
                                                                    {t(`apiDocs.webhooks.${code}.title`)}
                                                                </span>
                                                                <code>{code}</code>
                                                            </div>
                                                        </Accordion.Header>
                                                        <Accordion.Body>
                                                            <p className="mb-2">
                                                                {t(`apiDocs.webhooks.${code}.description`)}
                                                            </p>
                                                            {!!fieldEntries.length && (
                                                                <>
                                                                    <div className="fw-semibold small mb-1">
                                                                        {t('apiDocs.webhook.dataFields')}
                                                                    </div>
                                                                    <ul className="mb-0 small">
                                                                        {fieldEntries.map((field) => (
                                                                            <li key={field}>
                                                                                <code>{field}</code>
                                                                                {' — '}
                                                                                {t(
                                                                                    `apiDocs.webhooks.${code}.fields.${field}`,
                                                                                    {
                                                                                        defaultValue:
                                                                                            serverEvent?.dataFields?.[
                                                                                                field
                                                                                            ] || field,
                                                                                    },
                                                                                )}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </>
                                                            )}
                                                        </Accordion.Body>
                                                    </Accordion.Item>
                                                );
                                            })}
                                        </Accordion>
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>
                        </Card.Body>
                    </Card>
                )}
            </Col>
        </Row>
    );
};

export default ApiDocs;
